/** @jsx h */
/** @jsxFrag Fragment */
import { h } from "preact";
import { useMemo, useState } from "preact/hooks";
import type { ParamDict } from "@utils/params";
import { DatSubFolder, type FoldersContext } from "@uiw/dat";
import { DatParamGraphs, GraphsOf } from "@wbaudio/ui/graphs";
import {
  type EffectDef,
  type FxSlot,
  LiveEffects,
  type LiveEffectType,
  LiveEffectTypes,
} from "@wbaudio/core/synth";
import { DatRow, RowButton } from "./row.tsx";

export interface FxListActions {
  add: (type: LiveEffectType) => void;
  remove: (slot: string) => void;
  move: (slot: string, by: number) => void;
  bypass: (slot: string) => void;
  opts: (slot: string, opts: Record<string, unknown>) => void;
}

export interface FxSlotEditorProps {
  slot: FxSlot;
  index: number;
  count: number;
  actions: FxListActions;
  folders: FoldersContext;
  path: string;
}
export function FxSlotEditor(props: FxSlotEditorProps) {
  const { slot, actions } = props;
  const def = LiveEffects[slot.type] as EffectDef;
  const params = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(def.params).map(([k, p]) => [k, { ...p, value: slot.opts[k] ?? p.value }]),
      ) as ParamDict,
    [slot.type, slot.opts],
  );

  return (
    <DatSubFolder
      folders={props.folders}
      path={props.path}
      title={`${def.label}${slot.bypassed ? " (bypassed)" : ""}`}
    >
      <DatRow label="slot">
        <RowButton active={slot.bypassed} onClick={() => actions.bypass(slot.id)}>
          bypass
        </RowButton>
        {props.index > 0 && <RowButton onClick={() => actions.move(slot.id, -1)}>▲</RowButton>}
        {props.index < props.count - 1 && (
          <RowButton onClick={() => actions.move(slot.id, 1)}>▼</RowButton>
        )}
        <RowButton onClick={() => actions.remove(slot.id)}>✕</RowButton>
      </DatRow>
      <DatParamGraphs
        params={params}
        graphs={GraphsOf(def)}
        onChange={(opts) => actions.opts(slot.id, opts)}
      />
    </DatSubFolder>
  );
}

export interface FxListProps {
  slots: FxSlot[];
  actions: FxListActions;
  folders: FoldersContext;
  path: string;
  title: string;
}
export function FxList(props: FxListProps) {
  const [type, setType] = useState<LiveEffectType>("filter");

  return (
    <DatSubFolder folders={props.folders} path={props.path} title={props.title} hasSubfolders>
      <DatRow label="add effect">
        <select value={type} onChange={(e) => setType(e.currentTarget.value as LiveEffectType)}>
          {LiveEffectTypes.map((n) => <option key={n} value={n}>{LiveEffects[n].label}</option>)}
        </select>
        <RowButton onClick={() => props.actions.add(type)}>add</RowButton>
      </DatRow>
      {props.slots.map((slot, i) => (
        <FxSlotEditor
          key={slot.id}
          slot={slot}
          index={i}
          count={props.slots.length}
          actions={props.actions}
          folders={props.folders}
          path={`${props.path}/${slot.id}`}
        />
      ))}
    </DatSubFolder>
  );
}
